import { ChangeDetectorRef, Component, HostListener, Inject, NgZone, ViewChild, DestroyRef, inject } from '@angular/core'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import { MatStepper } from '@angular/material/stepper'
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper'
import { switchMap, tap } from 'rxjs'
import { AuthService } from 'src/app/services/auth.service'
import { ComissaoJulgadoraService } from '../comissao-julgadora-service'
import { UnespCoreMessageService } from 'src/libs/unesp-core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { DadosTermoConflito } from 'src/app/models/registro-de-acordo-examinador'
import { DeclarationComponent } from '../declaration/declaration.component'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'inscricoes-acordo-document-stepper-dialog',
  templateUrl: './acordo-document-stepper-dialog.component.html',
  styleUrls: ['./acordo-document-stepper-dialog.component.scss'],
  providers: [{ provide: STEPPER_GLOBAL_OPTIONS, useValue: { displayDefaultIndicatorType: false } }],
})
export class AcordoDocumentStepperDialogComponent {
  private destroyRef = inject(DestroyRef)

  @ViewChild('stepper') stepper!: MatStepper
  @ViewChild(DeclarationComponent) declaration?: DeclarationComponent

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { comissaoJulgadoraId: number; token: string },
    public dialogRef: MatDialogRef<AcordoDocumentStepperDialogComponent>,
    private authService: AuthService,
    private comissaoService: ComissaoJulgadoraService,
    private messageService: UnespCoreMessageService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private sanitizer: DomSanitizer
  ) {}

  externalLoading = false
  sendingDocument = false
  pdfViewBlob: Blob | null = null
  documentSent = false
  signedPdfUrl: SafeResourceUrl | null = null
  documentSentAt: Date | null = null
  private lastDeclaration: DadosTermoConflito | null = null
  private authCompletedForSigning = false

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.pdfViewBlob && !this.documentSent) {
      event.preventDefault()
    }
  }

  handleCompletedPreenchimento(declaration: DadosTermoConflito) {
    this.externalLoading = true
    this.lastDeclaration = declaration

    this.comissaoService
      .fetchDocumentoPreview(this.data.comissaoJulgadoraId, this.data.token, declaration)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.pdfViewBlob = data
          this.cdr.detectChanges()
          this.stepper.next()
        },
        error: error => {
          console.error('Error fetching preview:', error)
          this.messageService.showMessageError('Erro ao gerar pré-visualização. Tente novamente.')
          this.externalLoading = false
        },
        complete: () => {
          this.externalLoading = false
        },
      })
  }

  sendDocument() {
    if (!this.lastDeclaration) return
    this.sendingDocument = true

    const signAndSend = () => {
      this.comissaoService
        .fetchDocumentoDeAcordo(this.lastDeclaration!)
        .pipe(
          tap(signedPdf => (this.pdfViewBlob = signedPdf)),
          switchMap(signedPdf =>
            this.comissaoService.sendDeAcordoDocument(this.data.comissaoJulgadoraId, this.data.token, signedPdf)
          )
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.documentSent = true
            this.documentSentAt = new Date()
            this.sendingDocument = false
            this.cdr.detectChanges()
            this.stepper.next()
            this.signedPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(this.pdfViewBlob!) + '#navpanes=0')
          },
          error: (e: unknown) => {
            console.error('Error signing/sending document:', e)
            this.messageService.showMessageError('Erro ao assinar e enviar documento. Tente novamente.')
            this.sendingDocument = false
          },
        })
    }

    if (this.authCompletedForSigning) {
      signAndSend()
      return
    }

    this.authService
      .forceAuth()
      .then(() => this.ngZone.run(() => {
        this.authCompletedForSigning = true
        signAndSend()
      }))
      .catch(error => {
        console.error('Error during authentication:', error)
        this.sendingDocument = false
      })
  }

  downloadPdf() {
    if (!this.pdfViewBlob) return
    const url = URL.createObjectURL(this.pdfViewBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'declaracao-conflito-interesse.pdf'
    a.click()
    URL.revokeObjectURL(url)
  }

  onStepAnimationDone() {
    if (this.stepper.selectedIndex === 0) {
      (document.querySelector('#input-nacionalidade input') as HTMLElement)?.focus()
    }
  }
}
